import { Button, Checkbox, Css, IconButton, Tag, Tooltip, useModal } from "@homebound/beam";
import { ObjectState } from "@homebound/form-state";
import { observer } from "mobx-react";
import { ConfigureBidGroupsStepQuery } from "src/routes/bids/plan-based-bid-package/components/2.ConfigureBidGroupsStep.generated";
import { BasePlanPriceDimensionsModal } from "src/routes/bids/plan-based-bid-package/components/BasePlanPriceDimensionsModal";
import { BidPlanDimensionRows } from "src/routes/bids/plan-based-bid-package/components/BidPlanDimensionRows";
import { PlanBasedBidPackageFormState } from "src/routes/bids/plan-based-bid-package/utils";
import { pluralize } from "src/utils";

type BasePlanSectionProps = {
  formState: ObjectState<PlanBasedBidPackageFormState>;
  dimensionResults: ConfigureBidGroupsStepQuery["potentialBidPlanDimensions"];
  productOfferings: ConfigureBidGroupsStepQuery["bidPackage"]["productOfferings"];
};

export const BasePlanSection = observer(function BasePlanSection(props: BasePlanSectionProps) {
  const { formState, dimensionResults, productOfferings } = props;
  const { openModal } = useModal();
  // Keep the row in form state even when unchecked, we just flip its `archive` flag
  const basePlanRow = formState.bidPlanGroups.rows.find((g) => !g.globalOptionId.value);
  const basePlan = basePlanRow && !basePlanRow.archive.value ? basePlanRow : undefined;
  const hasBasePlan = !!basePlan;
  const hasDimensions = !!basePlan?.dimensions.rows.nonEmpty;
  const basePlanNames = (formState.productOfferings.value ?? [])
    .map((id) => productOfferings.find((po) => po.id === id)?.name)
    .filter((name): name is string => !!name);

  function toggleBasePlan(selected: boolean) {
    const groups = formState.bidPlanGroups.value ?? [];
    const existing = groups.find((g) => !g.globalOptionId);
    if (existing) {
      formState.bidPlanGroups.set(groups.map((g) => (g === existing ? { ...g, archive: !selected } : g)));
    } else if (selected) {
      formState.bidPlanGroups.set([...groups, { order: 0, archive: false, dimensions: [] }]);
    }
  }

  function openDimensionsModal() {
    if (!basePlan) return;
    openModal({ content: <BasePlanPriceDimensionsModal group={basePlan} dimensionResults={dimensionResults} /> });
  }

  return (
    <div css={Css.df.fdc.gap2.mb6.$}>
      <div css={Css.df.aic.jcsb.$}>
        <div css={Css.df.aic.gap1.$}>
          <Checkbox label="Base Plan" checkboxOnly selected={hasBasePlan} onChange={toggleBasePlan} />
          <div css={Css.lg.$}>Base Plan</div>
          {hasBasePlan && basePlanNames.nonEmpty && (
            <div css={Css.mya.$}>
              <Tag
                type="info"
                text={`${basePlanNames.length} ${pluralize(basePlanNames.length, "Product Offering")}`}
              />
            </div>
          )}
        </div>
        {hasBasePlan &&
          (hasDimensions ? (
            <IconButton icon="pencil" onClick={openDimensionsModal} />
          ) : (
            <Button icon="plus" label="Add Price Dimensions" variant="secondary" onClick={openDimensionsModal} />
          ))}
      </div>
      {hasBasePlan ? (
        <>
          {basePlanNames.nonEmpty && (
            <Tooltip title="Adjust which product offerings to include in this bid in step 1.">
              <div>{basePlanNames.join(", ")}</div>
            </Tooltip>
          )}
          {hasDimensions ? (
            <BidPlanDimensionRows group={basePlan} dimensionResults={dimensionResults} />
          ) : (
            <div css={Css.bgGray100.br8.py3.tac.gray700.$}>No price dimensions added.</div>
          )}
        </>
      ) : (
        <div css={Css.bgGray100.br8.py3.tac.gray700.$}>Base plans will not be included in this bid.</div>
      )}
    </div>
  );
});
